import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import { ErrorProperties } from '../../API/identityAPIProperties';
import API from '../../API/Main/API';
import { PaginationProperties, TableDataProperties } from '../../../shared/uiComponents/Table/tableProperties';
import { EventDataProperties, RecurringScheduleSessionProperties } from '../../API/ClientAPIHelpers/calendarProperties';
import { ScheduleCancellationViewModel } from '../../API/ClientAPIHelpers/scheduleCancellation';
import {
	AdminDashboardProperties,
	DashboardTemporaryAuthorizationProperties,
	InfoAvailableClientsProperties,
} from '../../API/ClientAPIHelpers/userClientProperties';

export interface BcbaDashboardSummary {
  totalClients: number;
  needsAttention: number;
  notesPendingSignOff: number;
}

export interface BcbaDashboardNextSession {
  eventId: string;
  sessionId: string | null;
  sessionTypeId: number;
  sessionTypeName: string | null;
  date: string;
  startTime: string;
  endTime: string;
  btName: string | null;
  isCompleted: boolean;
}

export interface BcbaDashboardTarget {
  id: string;
  name: string;
  status: {
    id: number;
    name: string;
  };
  trendStatus: string | null;
  trendStatusLabel: string | null;
  latestPercentage: number | null;
}

export interface BcbaDashboardGoalProgress {
  programId: string;
  programName: string;
  skillAreaName: string | null;
  targets: BcbaDashboardTarget[];
}

export interface BcbaAuthorizationAllocation {
  sessionTypeName: string;
  totalHours: number;
  hoursPerWeek: number;
}

export interface BcbaAuthorizationDetail {
  startDate: string;
  endDate: string;
  allocations: BcbaAuthorizationAllocation[];
}

export interface BcbaDashboardClient {
  client: InfoAvailableClientsProperties;
  activeTargetCount: number;
  authorizationExpiry: string | null;
  decliningTargets: number;
  improvingTargets: number;
  nearingMasteryTargets: number;
  nextSession: BcbaDashboardNextSession | null;
  goalsCount: number;
  weeklyBillingInsuranceName: string | null;
  billingWeekStart: string | null;
  billingWeekEnd: string | null;
}

export interface BcbaDashboard {
  summary: BcbaDashboardSummary;
  upcomingReassessmentsCount: number;
  clients: BcbaDashboardClient[];
}

export interface BtDashboardSession {
  sessionId: string | null;
  eventId: string;
  client: InfoAvailableClientsProperties;
  startTime: string;
  endTime: string;
  locationName: string | null;
  locationAddress: string | null;
  isTelehealth: boolean;
  activeTargetsCount: number;
  isCompleted: boolean;
  sessionTypeId: number;
  isRecurringScheduleOccurrence: boolean;
  recurringScheduleId: string | null;
  recurringScheduleTimeSlotId?: string | null;
  sessionTypeType: number;
}

export interface BtDashboardSummary {
  sessionsCompletedThisWeek: number;
  sessionsScheduledThisWeek: number;
  sessionsMissedThisWeek: number;
  hoursWorkedThisWeek: number;
  targetsImproving: number;
}

export interface IncompleteNotesSummary {
  count: number;
  draftCount: number;
  notStartedCount: number;
  soonestExpiryHours: number | null;
}

export interface BtClient {
  client: InfoAvailableClientsProperties;
  sessionsPerWeek: number;
  activeTargetsCount: number;
  scheduleDays: number[];
}

export interface BtDashboard {
  sessions: Array<BtDashboardSession>;
  summary: BtDashboardSummary;
  incompleteNotes: IncompleteNotesSummary;
  clients: Array<BtClient>;
  recurringSchedules: RecurringScheduleSessionProperties[];
  cancellations: ScheduleCancellationViewModel[];
}

export interface CsDashboardSummary {
  clientCount: number;
}

export interface CsClientDetail {
  client: InfoAvailableClientsProperties;
  authEndDate: string | null;
  isReauthDue: boolean;
  weeksInactive: number | null;
  activeTargetCount: number;
  btHoursDelivered: number;
  btHoursAuthorized: number;
  supervisionRatioPct: number;
  parentTrainingRatioPct: number;
  lastReviewedDate: string | null;
}

export interface CsBcbaCard {
  userId: string;
  fullName: string;
  clientCount: number;
  supervisionRatioPct: number;
  parentTrainingRatioPct: number;
  upcomingAssessmentCount: number;
  clients: CsClientDetail[];
}

export interface CsAssessmentPipelineItem {
  client: InfoAvailableClientsProperties;
  bcbaName: string;
  authStartDate: string;
  authEndDate: string;
  status: string;
  reportId: string | null;
  sessionId: string | null;
}

export interface CsClientInactivityItem {
  client: InfoAvailableClientsProperties;
  bcbaName: string;
  weeksInactive: number;
  lastSessionDate: string | null;
  authEndDate: string | null;
}

export interface CsFlaggedSession {
  sessionId: string;
  clientId: string;
  clientName: string;
  isElc: boolean;
  userId: string;
  userName: string;
  sessionTypeName: string;
  date: string;
  flagReason: 0 | 1 | 2;
  reportId: string | null;
}

export interface CsDashboard {
  summary: CsDashboardSummary;
  bcbas: CsBcbaCard[];
  assessmentPipeline: CsAssessmentPipelineItem[];
  clientInactivity: CsClientInactivityItem[];
}

export interface CsDashboardSummary {
  clientCount: number;
}

export interface CsClientDetail {
  client: InfoAvailableClientsProperties;
  authEndDate: string | null;
  isReauthDue: boolean;
  weeksInactive: number | null;
  activeTargetCount: number;
  btHoursDelivered: number;
  btHoursAuthorized: number;
  supervisionRatioPct: number;
  parentTrainingRatioPct: number;
  lastReviewedDate: string | null;
}

export interface CsBcbaCard {
  userId: string;
  fullName: string;
  clientCount: number;
  supervisionRatioPct: number;
  parentTrainingRatioPct: number;
  upcomingAssessmentCount: number;
  clients: CsClientDetail[];
}

export interface CsAssessmentPipelineItem {
  client: InfoAvailableClientsProperties;
  bcbaName: string;
  authStartDate: string;
  authEndDate: string;
  status: string;
  reportId: string | null;
  sessionId: string | null;
}

export interface CsClientInactivityItem {
  client: InfoAvailableClientsProperties;
  bcbaName: string;
  weeksInactive: number;
  lastSessionDate: string | null;
  authEndDate: string | null;
}

export interface CsFlaggedSession {
  sessionId: string;
  clientId: string;
  clientName: string;
  isElc: boolean;
  userId: string;
  userName: string;
  sessionTypeName: string;
  date: string;
  flagReason: 0 | 1 | 2;
  reportId: string | null;
}

export interface CsDashboard {
  summary: CsDashboardSummary;
  bcbas: CsBcbaCard[];
  assessmentPipeline: CsAssessmentPipelineItem[];
  clientInactivity: CsClientInactivityItem[];
}

export interface FlagggedSessionsTableData extends TableDataProperties {
	query: EventDataProperties[] | null;
}

interface DashboardStateProperties {
  authorizations: AdminDashboardProperties;
  bcbaDashboard: BcbaDashboard | null;
  btDashboard: BtDashboard | null;
  csDashboard: CsDashboard | null;
  csFlaggedSessions: CsFlaggedSession[];
  csFlaggedSessionsLoading: boolean;
  error: ErrorProperties;
  loading: boolean;
  sessions: FlagggedSessionsTableData;
  temporaryAuthorizations: DashboardTemporaryAuthorizationProperties;
}

const initialDashboardState: DashboardStateProperties = {
  loading: false,
  sessions: { query: null },
  authorizations: { query: null },
  temporaryAuthorizations: { query: null },
  bcbaDashboard: null,
  btDashboard: null,
  csDashboard: null,
  csFlaggedSessions: [],
  csFlaggedSessionsLoading: false,
  error: {
    title: "",
    status: 0,
  },
};

export const getTemporaryAuthorizations = createAsyncThunk(
	'/dashboard/admin/temporary',
	async (data: PaginationProperties, thunkAPI) => {
		const response = await API.ClientAPI.Dashboard.getTemporaryAuthorizations(data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response as DashboardTemporaryAuthorizationProperties;
	},
);

export const getFlaggedSessions = createAsyncThunk(
	'/dashboard/admin/flagged',
	async (data: PaginationProperties, thunkAPI) => {
		const response = await API.ClientAPI.Dashboard.getFlaggedSessions(data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response as FlagggedSessionsTableData;
	},
);

export const getBcbaDashboard = createAsyncThunk('/dashboard/bcba', async (_, thunkAPI) => {
	const response = await API.ClientAPI.Dashboard.getBcbaDashboard();

	if (response.error) {
		return thunkAPI.rejectWithValue(response.error);
	}

	return response as BcbaDashboard;
});

export const getAuthorizationInfoClinicalSupervisor = createAsyncThunk(
	'/dashboard/cs',
	async ({ userId, data }: { data: PaginationProperties; userId: string }, thunkAPI) => {
		const response = await API.ClientAPI.Dashboard.getAuthorizationInfoClinicalSupervisor({
			userId,
			data,
		});

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response as AdminDashboardProperties;
	},
);

export const getAuthorizationInfoAdmin = createAsyncThunk(
	'/dashboard/admin',
	async (data: PaginationProperties, thunkAPI) => {
		const response = await API.ClientAPI.Dashboard.getAuthorizationInfoAdmin(data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response as AdminDashboardProperties;
	},
);

export const getCsDashboard = createAsyncThunk(
	'/dashboard/cs-overview',
	async (_, thunkAPI) => {
		const response = await API.ClientAPI.Dashboard.getCsDashboard();

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response as CsDashboard;
	},
);

export const getCsFlaggedSessions = createAsyncThunk(
	'/dashboard/cs/flagged-sessions',
	async (_, thunkAPI) => {
		const response = await API.ClientAPI.Dashboard.getCsFlaggedSessions();

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response as CsFlaggedSession[];
	},
);

export const getBtDashboard = createAsyncThunk(
	'/dashboard/bt',
	async (_, thunkAPI) => {
		const response = await API.ClientAPI.Dashboard.getBtDashboard();

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		const dashboard = response as BtDashboard;
		const providerIds = Array.from(new Set((dashboard.recurringSchedules ?? []).map((schedule) => schedule.providerId).filter(Boolean)));
		let cancellations: ScheduleCancellationViewModel[] = [];

		try {
			if (providerIds.length) {
				const today = dayjs().format('YYYY-MM-DD');
				const cancellationResponse = await API.ClientAPI.ScheduleCancellation.getCancellationsForCalendar({
					providerIds,
					startDate: today,
					endDate: today,
				});
				if (!cancellationResponse.error && Array.isArray(cancellationResponse)) cancellations = cancellationResponse;
			}
		} catch {
			cancellations = [];
		}

		return { ...dashboard, cancellations };
	},
);

const dashboardSlice = createSlice({
  initialState: initialDashboardState,
  name: "dashboard",
  reducers: {},
  extraReducers: (builder) => {
    //getTemporaryAuthorizations
    builder.addCase(getTemporaryAuthorizations.pending, (state) => {
      state.loading = true;
      state.error = initialDashboardState.error;
    });
    builder.addCase(getTemporaryAuthorizations.fulfilled, (state, action) => {
      state.loading = false;
      state.temporaryAuthorizations = action.payload;
    });
    builder.addCase(getTemporaryAuthorizations.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as ErrorProperties;
    });
    //getFlaggedSessions
    builder.addCase(getFlaggedSessions.pending, (state) => {
      state.loading = true;
      state.error = initialDashboardState.error;
    });
    builder.addCase(getFlaggedSessions.fulfilled, (state, action) => {
      state.loading = false;
      state.sessions = action.payload;
    });
    builder.addCase(getFlaggedSessions.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as ErrorProperties;
    });
    //getBcbaDashboard
    builder.addCase(getBcbaDashboard.pending, (state) => {
      state.loading = true;
      state.error = initialDashboardState.error;
    });
    builder.addCase(getBcbaDashboard.fulfilled, (state, action) => {
      state.loading = false;
      state.bcbaDashboard = action.payload;
    });
    builder.addCase(getBcbaDashboard.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as ErrorProperties;
    });
    //getAuthorizationInfoClinicalSupervisor
    builder.addCase(getAuthorizationInfoClinicalSupervisor.pending, (state) => {
      state.loading = true;
      state.error = initialDashboardState.error;
    });
    builder.addCase(getAuthorizationInfoClinicalSupervisor.fulfilled, (state, action) => {
      state.loading = false;
      state.authorizations = action.payload;
    });
    builder.addCase(getAuthorizationInfoClinicalSupervisor.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as ErrorProperties;
    });
    //getAuthorizationInfoAdmin
    builder.addCase(getAuthorizationInfoAdmin.pending, (state) => {
      state.loading = true;
      state.error = initialDashboardState.error;
    });
    builder.addCase(getAuthorizationInfoAdmin.fulfilled, (state, action) => {
      state.loading = false;
      state.authorizations = action.payload;
    });
    builder.addCase(getAuthorizationInfoAdmin.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as ErrorProperties;
    });
    //getCsDashboard
    builder.addCase(getCsDashboard.pending, (state) => {
      state.loading = true;
      state.error = initialDashboardState.error;
    });
    builder.addCase(getCsDashboard.fulfilled, (state, action) => {
      state.loading = false;
      state.csDashboard = action.payload;
    });
    builder.addCase(getCsDashboard.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as ErrorProperties;
    });
    //getCsFlaggedSessions
    builder.addCase(getCsFlaggedSessions.pending, (state) => {
      state.csFlaggedSessionsLoading = true;
      state.error = initialDashboardState.error;
    });
    builder.addCase(getCsFlaggedSessions.fulfilled, (state, action) => {
      state.csFlaggedSessionsLoading = false;
      state.csFlaggedSessions = action.payload;
    });
    builder.addCase(getCsFlaggedSessions.rejected, (state, action) => {
      state.csFlaggedSessionsLoading = false;
      state.error = action.payload as ErrorProperties;
    });
    //getBtDashboard
    builder.addCase(getBtDashboard.pending, (state) => {
      state.loading = true;
      state.error = initialDashboardState.error;
    });
    builder.addCase(getBtDashboard.fulfilled, (state, action) => {
      state.loading = false;
      state.btDashboard = action.payload;
    });
    builder.addCase(getBtDashboard.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as ErrorProperties;
    });
  },
});

export default dashboardSlice.reducer;
